import { TimeRange } from '@mi-front/core';
import { PropsWithChildren, useContext, useEffect, useMemo } from 'react';
import { PlaybackStoreContext } from '../context/playback.context';
import { PlaybackProvider } from '../context/PlaybackProvider';
import { ExclusivePlaybackMode } from '../store/base-playback-slice';
import { createVideoPlaybackStore } from './useVideoPlaybackStore';

export type VideoPlaybackProviderProps = {
  /** Optional source URL for media */
  src?: string;
  /** Optional playback range for bounded playback (e.g., fragment chunks) */
  playbackRange?: TimeRange<number>;
  exclusivePlaybackMode?: ExclusivePlaybackMode;
  /** Optional identifier for this playback instance, shown in debug logs */
  playbackId?: string;
};

/**
 * Provider component that creates a video playback store and passes it to PlaybackProvider.
 * Used for video playback with optional source URL and playback range.
 */
export function VideoJsPlaybackProvider({
  src,
  playbackRange,
  children,
  exclusivePlaybackMode = 'pause',
  playbackId,
}: PropsWithChildren<VideoPlaybackProviderProps>) {
  // Create store exactly once per component instance
  const videoStore = useMemo(
    () =>
      createVideoPlaybackStore({
        src,
        playbackRange,
        exclusivePlaybackMode,
        playbackId,
      }),
    [],
  );

  // When nested inside another playback context (e.g. logger stream), register this store with the parent
  const ctx = useContext(PlaybackStoreContext)!;

  useEffect(() => {
    ctx?.setStore(videoStore);
  }, []);

  // Root usage: no parent context — provide our own PlaybackProvider
  if (!ctx) {
    return <PlaybackProvider store={videoStore}>{children}</PlaybackProvider>;
  }

  // Nested usage: only render children after parent context points to this exact store
  // to avoid mounting video consumers while an audio store is still active.
  return ctx.store === videoStore ? children : null;
}
